import RequestBuilder from 'Resources/RequestBuilder';
import Signals from 'Resources/Signals';
import { AppThunk } from '..';
import { CancelablePromise, makeCancelable } from 'Helpers/promise';

export enum FilterDate {
	ALL = 'all',
	MONTH = 'month',
	WEEK = 'week'
}

export enum FilterType {
	ALL = 'all',
	NEWS = 'news',
	UPDATES = 'updates',
	RISK = 'risk'
}

export enum FilterView {
	USER = 'user',
	ALL = 'all'
}

export const RESET = '[SignalsFeed] RESET';
export const SET_STATE = '[SignalsFeed] SET_STATE';

export const initialState = {
	filter: FilterType.RISK,
	date: FilterDate.WEEK,
	view: FilterView.USER,
	loading: false,
	offset: 0,
	limit: 50,
	total: 0,
	items: [],
	languages: ['sv'],
	topics: []
};

export type SignalsFeedFilters = {
	filter: FilterType;
	date: FilterDate;
	view: FilterView;
	languages: string[];
	topics: string[];
	clientFilters?: RequestBuilder;
	offset: number;
	limit: number;
};

export type SignalsFeedState = SignalsFeedFilters & {
	loading: boolean;
	items?: any[];
	total: number;
};

let fetchPromise: CancelablePromise<Awaited<ReturnType<typeof Signals.getFeed>>> | null = null;

const getData = ({ view, filter, date, limit, offset, languages, topics, clientFilters }: SignalsFeedFilters) => {
	if (fetchPromise) {
		fetchPromise.cancel();
	}
	fetchPromise = makeCancelable(
		Signals.getFeed(view, filter, date, limit, offset, languages, topics, clientFilters?.build())
	);
	return fetchPromise.promise;
};

export const init =
	(partialState?: Partial<SignalsFeedFilters>): AppThunk =>
	async (dispatch, getState) => {
		dispatch({ type: SET_STATE, loading: true, ...partialState });
		const filters = {
			...getState().SignalsFeed,
			...partialState
		};
		try {
			const { data, metadata } = await getData(filters);
			dispatch({ type: SET_STATE, items: data, total: metadata.total || 0, loading: false });
		} catch {
			dispatch({ type: SET_STATE, items: [], total: 0, loading: false });
		}
	};

export const setState =
	(partialState: Partial<SignalsFeedState>): AppThunk =>
	dispatch => {
		dispatch({ type: SET_STATE, ...partialState });
	};

export const onChangeFilters =
	(filters: Partial<SignalsFeedFilters>): AppThunk =>
	async (dispatch, getState) => {
		dispatch({ type: SET_STATE, ...filters, loading: true });
		const state = getState().SignalsFeed;
		const finalFilters = { ...state, ...filters };

		try {
			const { data, metadata } = await getData(finalFilters);
			dispatch({ type: SET_STATE, items: data, total: metadata.total || 0, loading: false });
		} catch {
			dispatch({ type: SET_STATE, items: [], total: 0, loading: false });
		}
	};

type ActionHandler = (
	state: SignalsFeedState,
	action: Partial<SignalsFeedState> & { type: string }
) => SignalsFeedState;

const ACTION_HANDLERS: { [key: string]: ActionHandler } = {
	[RESET]: () => ({ ...initialState }),
	[SET_STATE]: (state, { type, ...newState }) => ({ ...state, ...newState })
};

const SignalsFeedReducer = (state: SignalsFeedState = initialState, action: SignalsFeedState & { type: string }) => {
	const handler = ACTION_HANDLERS[action.type];
	return handler ? handler(state, action) : state;
};

export default SignalsFeedReducer;
